import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSmartNavigate } from '@/lib/nav-prefs/use-smart-navigate';
import {
  Badge, Button, Card, CardContent, CardDescription, CardHeader, CardTitle,
  DataTable, type ColumnDef,
} from '@erp/ui';
import { ArrowRight } from '@/components/icons';
import { useSalesDocuments } from '@/hooks/use-sales';
import { usePurchaseDocuments } from '@/hooks/use-purchases';
import { useClients } from '@/hooks/use-clients';
import { useSuppliers } from '@/hooks/use-suppliers';
import type {
  Client, Supplier, SalesDocument, PurchaseDocument,
  SalesDocType, PurchaseDocType,
} from '@erp/data-access/interfaces';

type DocSide = 'sales' | 'purchase';

interface ActivityRow {
  id: string;
  side: DocSide;
  docType: SalesDocType | PurchaseDocType;
  number: string;
  docDate: string;
  thirdPartyName: string;
  totalTtc: number;
  isInvoiced: boolean;
  href: string;
}

const SALES_TYPE_KEY: Record<SalesDocType, string> = {
  quote: 'nav.quotes',
  order: 'nav.orders',
  delivery: 'nav.deliveries',
  invoice: 'nav.invoices',
  credit_note: 'nav.creditNotes',
};

const PURCHASE_TYPE_KEY: Record<PurchaseDocType, string> = {
  order: 'nav.purchaseOrders',
  reception: 'nav.receptions',
  invoice: 'nav.purchaseInvoices',
  credit_note: 'nav.purchaseCreditNotes',
};

const SALES_HREF: Record<SalesDocType, string> = {
  quote: '/sales/quotes',
  order: '/sales/orders',
  delivery: '/sales/deliveries',
  invoice: '/sales/invoices',
  credit_note: '/sales/credit-notes',
};

const PURCHASE_HREF: Record<PurchaseDocType, string> = {
  order: '/purchases/orders',
  reception: '/purchases/receptions',
  invoice: '/purchases/invoices',
  credit_note: '/purchases/credit-notes',
};

function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(lang === 'ar' ? 'ar-TN' : 'fr-TN', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatPrice(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString('fr-TN', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
}

export function RecentActivityTable() {
  const { t, i18n } = useTranslation();
  const navigate = useSmartNavigate();

  const { data: quotes } = useSalesDocuments({ docType: 'quote' });
  const { data: orders } = useSalesDocuments({ docType: 'order' });
  const { data: deliveries } = useSalesDocuments({ docType: 'delivery' });
  const { data: invoices } = useSalesDocuments({ docType: 'invoice' });
  const { data: creditNotes } = useSalesDocuments({ docType: 'credit_note' });
  const { data: pOrders } = usePurchaseDocuments({ docType: 'order' });
  const { data: pReceptions } = usePurchaseDocuments({ docType: 'reception' });
  const { data: pInvoices } = usePurchaseDocuments({ docType: 'invoice' });
  const { data: pCreditNotes } = usePurchaseDocuments({ docType: 'credit_note' });
  const { data: clients } = useClients();
  const { data: suppliers } = useSuppliers();

  const rows = useMemo<ActivityRow[]>(() => {
    const clientMap = new Map<string, Client>((clients ?? []).map((c) => [c.id, c]));
    const supplierMap = new Map<string, Supplier>((suppliers ?? []).map((s) => [s.id, s]));

    const allSales: SalesDocument[] = [
      ...(quotes ?? []), ...(orders ?? []), ...(deliveries ?? []),
      ...(invoices ?? []), ...(creditNotes ?? []),
    ];
    const allPurchases: PurchaseDocument[] = [
      ...(pOrders ?? []), ...(pReceptions ?? []), ...(pInvoices ?? []), ...(pCreditNotes ?? []),
    ];

    const salesRows: ActivityRow[] = allSales.map((d) => ({
      id: `s-${d.id}`,
      side: 'sales' as const,
      docType: d.docType,
      number: d.number,
      docDate: d.docDate,
      thirdPartyName: clientMap.get(d.clientId)?.name ?? '—',
      totalTtc: d.totalTtc,
      isInvoiced: d.isInvoiced,
      href: SALES_HREF[d.docType],
    }));
    const purchaseRows: ActivityRow[] = allPurchases.map((d) => ({
      id: `p-${d.id}`,
      side: 'purchase' as const,
      docType: d.docType,
      number: d.number,
      docDate: d.docDate,
      thirdPartyName: supplierMap.get(d.supplierId)?.name ?? '—',
      totalTtc: d.totalTtc,
      isInvoiced: d.isInvoiced,
      href: PURCHASE_HREF[d.docType],
    }));

    return [...salesRows, ...purchaseRows]
      .sort((a, b) => b.docDate.localeCompare(a.docDate))
      .slice(0, 50);
  }, [quotes, orders, deliveries, invoices, creditNotes, pOrders, pReceptions, pInvoices, pCreditNotes, clients, suppliers]);

  const columns: ColumnDef<ActivityRow, unknown>[] = [
    {
      id: 'date',
      accessorKey: 'docDate',
      header: t('dashboard.date'),
      cell: ({ row }) => (
        <span className="tabular-nums text-muted-foreground">{formatDate(row.original.docDate, i18n.language)}</span>
      ),
    },
    {
      id: 'type',
      header: t('dashboard.type'),
      cell: ({ row }) => {
        const r = row.original;
        const key = r.side === 'sales'
          ? SALES_TYPE_KEY[r.docType as SalesDocType]
          : PURCHASE_TYPE_KEY[r.docType as PurchaseDocType];
        const tone = r.side === 'sales' ? 'border-emerald-500/40 text-emerald-700 dark:text-emerald-400' : 'border-amber-500/40 text-amber-700 dark:text-amber-400';
        return <Badge variant="outline" className={tone}>{t(key)}</Badge>;
      },
    },
    {
      id: 'number',
      accessorKey: 'number',
      header: t('dashboard.invoiceId'),
      cell: ({ row }) => <span className="font-medium tabular-nums">{row.original.number}</span>,
    },
    {
      id: 'tier',
      accessorKey: 'thirdPartyName',
      header: t('dashboard.customer'),
      cell: ({ row }) => <span className="truncate">{row.original.thirdPartyName}</span>,
    },
    {
      id: 'amount',
      accessorKey: 'totalTtc',
      header: () => <div className="text-end">{t('dashboard.amount')}</div>,
      cell: ({ row }) => (
        <div className="text-end tabular-nums font-medium">{formatPrice(row.original.totalTtc)} <span className="text-muted-foreground font-normal text-xs">DT</span></div>
      ),
    },
    {
      id: 'status',
      header: t('dashboard.status'),
      cell: ({ row }) => {
        const r = row.original;
        if (r.docType === 'delivery' || r.docType === 'reception' || r.docType === 'order') {
          return r.isInvoiced
            ? <Badge variant="outline" className="text-emerald-600">{t('dashboard.invoiced')}</Badge>
            : <Badge variant="outline" className="text-muted-foreground">{t('dashboard.uninvoiced')}</Badge>;
        }
        return <span className="text-muted-foreground text-xs">—</span>;
      },
    },
    {
      id: 'open',
      header: '',
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => navigate(row.original.href)}
          aria-label={t('common.open')}
        >
          <ArrowRight size={14} className="rtl:-scale-x-100" />
        </Button>
      ),
    },
  ];

  return (
    <Card className="@container/card">
      <CardHeader>
        <CardTitle>{t('dashboard.recentDocuments')}</CardTitle>
        <CardDescription>{t('dashboard.recentDocumentsHelp')}</CardDescription>
      </CardHeader>
      <CardContent>
        <DataTable
          columns={columns}
          data={rows}
          pagination
          pageSize={10}
          rowsPerPageLabel={t('dashboard.rowsPerPage')}
          ofLabel={t('dashboard.of')}
          emptyMessage={t('dashboard.noResults')}
          minWidth={720}
        />
      </CardContent>
    </Card>
  );
}
